// Landing-page dictionary, ported verbatim from the original landing.js `landingI18n`.
// The standalone landing page diverges from the in-app i18n for the trial/steps/contact
// copy (Google-first wording), so it needs its own dictionary with fallback to the main.
import { translate } from "$lib/i18n";
import type { LanguageTag } from "$lib/i18n/dictionary";

const landingI18n: Record<LanguageTag, Record<string, string>> = {
  zh: {
    language: "語言",
    appTitle: "家庭週預算",
    landingEyebrow: "私人試用版本",
    landingHeadline: "每週預算，一眼看清。",
    landingCopy: "以簡單、清楚、優雅的方式處理月度掌握、每週輸入與交易檢視。",
    continueWithGoogle: "使用 Google 繼續",
    landingStartWithGoogle: "使用 Google 開始",
    landingContactUs: "聯絡我們",
    landingExploreTitle: "建議先看這些內容",
    landingGuideLink: "使用方式",
    landingFaqLink: "常見問題",
    landingPrivacyLink: "隱私權",
    landingTermsLink: "使用條款",
    landingPillStatement: "月結摘要",
    landingPillTrend: "趨勢圖表",
    landingPillReview: "交易檢視",
    landingTrialEyebrow: "試用方式",
    landingTrialTitle: "從 Google 開始",
    landingStep1Title: "使用 Google 繼續",
    landingStep1Copy: "使用已驗證的 Google 帳號，不需要另外申請試用密碼。",
    landingStep2Title: "直接開啟試用",
    landingStep2Copy: "系統會自動為你準備帳號與乾淨的工作區。",
    landingStep3Title: "之後再升級正式權限",
    landingStep3Copy: "管理者可在檢視後，將試用使用者升級為正式使用者。",
    landingContactEyebrow: "聯絡我們",
    landingContactTitle: "支援與導入協助",
    landingContactCopy: "新使用者可直接透過 Google 開始試用。如需正式權限、導入協助，或既有帳號支援，請聯絡你的服務管理者或示範主持人。",
    landingContactAction: "前往登入選項",
    landingContactEmailBtn: "寄信給我們",
    landingPrivateWorkspace: "私人工作區",
    landingLoginTitle: "登入以繼續",
    landingLoginButton: "登入",
    landingOpenWorkspace: "開啟工作區",
    landingAuthCopySignedIn: "你已經登入，可以直接進入工作區。",
    landingAuthCopyGoogle: "使用 Google 繼續，以建立或開啟你的試用工作區。",
    landingAuthCopyOpen: "這個環境目前為開放模式，可以直接進入工作區。",
    landingAuthCopyPassword: "繼續前往私人工作區。密碼將在安全的應用程式登入畫面中輸入。",
    landingAuthFootnoteGoogle: "Google 試用使用者會自動取得乾淨工作區。既有帳號仍可使用密碼登入。",
    landingAuthFootnotePassword: "密碼登入保留給既有管理員或內部帳號。",
    landingAuthFootnoteOpen: "此環境目前開放試用，你可以直接進入工作區。",
    landingHighlightsAria: "產品亮點",
    landingVisualAria: "真實產品畫面",
    landingStatementImageAlt: "家庭週預算月結摘要畫面",
    landingTrendImageAlt: "家庭週預算月支出趨勢圖畫面",
  },
  en: {
    language: "Language",
    appTitle: "Family Weekly Budget",
    landingEyebrow: "Private trial release",
    landingHeadline: "Weekly budgeting, clearly presented.",
    landingCopy: "A simple private workspace for monthly oversight, weekly entry, and transaction review.",
    continueWithGoogle: "Continue with Google",
    landingStartWithGoogle: "Start with Google",
    landingContactUs: "Contact us",
    landingExploreTitle: "Recommended pages",
    landingGuideLink: "How it works",
    landingFaqLink: "FAQ",
    landingPrivacyLink: "Privacy",
    landingTermsLink: "Terms",
    landingPillStatement: "Monthly statement",
    landingPillTrend: "Trend graph",
    landingPillReview: "Transaction review",
    landingTrialEyebrow: "Trial access",
    landingTrialTitle: "Start with Google",
    landingStep1Title: "Continue with Google",
    landingStep1Copy: "Use a verified Google account. No trial password is required.",
    landingStep2Title: "Open your trial",
    landingStep2Copy: "Your account and clean workspace are prepared automatically.",
    landingStep3Title: "Grow into standard access",
    landingStep3Copy: "The owner can upgrade trial users after review.",
    landingContactEyebrow: "Contact us",
    landingContactTitle: "Support and onboarding",
    landingContactCopy: "New users can start a trial with Google. If you need standard access, onboarding support, or help with an existing account, contact your service owner or demo host.",
    landingContactAction: "Go to login options",
    landingContactEmailBtn: "Send us an email",
    landingPrivateWorkspace: "Private workspace",
    landingLoginTitle: "Log in to continue",
    landingLoginButton: "Log in",
    landingOpenWorkspace: "Open workspace",
    landingAuthCopySignedIn: "You are already signed in. Continue directly to the workspace.",
    landingAuthCopyGoogle: "Continue with Google to create or open your trial workspace.",
    landingAuthCopyOpen: "This preview is currently open. Continue directly to the workspace.",
    landingAuthCopyPassword: "Continue to the private workspace. You will enter your password on the secure app login screen.",
    landingAuthFootnoteGoogle: "Google trial users get a clean workspace automatically. Password login remains available for existing accounts.",
    landingAuthFootnotePassword: "Password login is reserved for existing owner or internal accounts.",
    landingAuthFootnoteOpen: "Trial access is open in this environment. You can enter the workspace directly.",
    landingHighlightsAria: "Product highlights",
    landingVisualAria: "Real product screenshots",
    landingStatementImageAlt: "Family Weekly Budget monthly statement highlight",
    landingTrendImageAlt: "Family Weekly Budget monthly spending trend graph highlight",
  },
};

export function translateLanding(lang: LanguageTag, key: string): string {
  return landingI18n[lang]?.[key] ?? landingI18n.en[key] ?? translate(lang, key);
}
